"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Events = void 0;
const SdkExtension_1 = __importDefault(require("@rc-ex/core/lib/SdkExtension"));
const isomorphic_ws_1 = __importDefault(require("isomorphic-ws"));
const hyperid_1 = __importDefault(require("hyperid"));
const events_1 = require("events");
const wait_for_async_1 = __importDefault(require("wait-for-async"));
const RestException_1 = __importDefault(require("@rc-ex/core/lib/RestException"));
const rest_1 = require("./rest");
const subscription_1 = __importDefault(require("./subscription"));
const ConnectionException_1 = __importDefault(require("./exceptions/ConnectionException"));
const utils_1 = __importDefault(require("./utils"));
const CONNECTING = 0;
const OPEN = 1;
const uuid = (0, hyperid_1.default)();
var Events;
(function (Events) {
    Events["autoRecoverSuccess"] = "autoRecoverSuccess";
    Events["autoRecoverFailed"] = "autoRecoverFailed";
    Events["autoRecoverError"] = "autoRecoverError";
    Events["newWebSocketObject"] = "newWebSocketObject";
    Events["newWsc"] = "newWsc";
    Events["connectionReady"] = "connectionReady";
})(Events = exports.Events || (exports.Events = {}));
class WebSocketExtension extends SdkExtension_1.default {
    constructor(options = {}) {
        var _a, _b, _c, _d, _e;
        var _f, _g, _h, _j, _k;
        super();
        this.eventEmitter = new events_1.EventEmitter();
        this.wsTokenExpiresAt = 0;
        this.request = rest_1.request; // request method was moved to another file to keep this file short
        this.options = options;
        (_a = (_f = this.options).restOverWebSocket) !== null && _a !== void 0 ? _a : (_f.restOverWebSocket = false);
        (_b = (_g = this.options).debugMode) !== null && _b !== void 0 ? _b : (_g.debugMode = false);
        (_c = (_h = this.options).autoRecover) !== null && _c !== void 0 ? _c : (_h.autoRecover = {
            enabled: true,
        });
        (_d = (_j = this.options.autoRecover).checkInterval) !== null && _d !== void 0 ? _d : (_j.checkInterval = (retriesAttempted) => {
            const interval = 2000 + 2000 * retriesAttempted;
            return Math.min(8000, interval);
        });
        (_e = (_k = this.options.autoRecover).pingServerInterval) !== null && _e !== void 0 ? _e : (_k.pingServerInterval = 30000);
    }
    disable() {
        super.disable();
        if (this.subscription) {
            this.subscription.enabled = false;
        }
    }
    async install(rc) {
        this.rc = rc;
        if (this.options.restOverWebSocket) {
            const request = rc.request.bind(rc);
            rc.request = async (method, endpoint, content, queryParams, config) => {
                var _a, _b, _c;
                if (!this.enabled || !this.options.restOverWebSocket) {
                    return request(method, endpoint, content, queryParams, config);
                }
                if (
                // the following cannot be done with WebSocket
                ((_c = ((_b = (_a = config === null || config === void 0 ? void 0 : config.headers) === null || _a === void 0 ? void 0 : _a.getContentType) === null || _b === void 0 ? void 0 : _b.toString())) === null || _c === void 0 ? void 0 : _c.includes('multipart/form-data'))
                    || (config === null || config === void 0 ? void 0 : config.responseType) === 'arraybuffer'
                    || endpoint.startsWith('/restapi/oauth/') // token, revoke, wstoken
                ) {
                    return request(method, endpoint, content, queryParams, config);
                }
                return this.request(method, endpoint, content, queryParams, config);
            };
        }
        // should recover if this.options.wscToken
        let connectMethod = this.connect.bind(this);
        if (this.options.wscToken) {
            this.wsc = {
                token: this.options.wscToken,
                sequence: 0,
            };
            connectMethod = this.recover.bind(this);
        }
        if (!this.options.autoRecover.enabled) {
            await connectMethod();
            return;
        }
        // code after is for auto recover
        try {
            await connectMethod();
        }
        catch (e) {
            if (e instanceof RestException_1.default) {
                throw e; // such as InsufficientPermissions
            }
            if (this.options.debugMode) {
                console.debug('Initial connect failed:', e);
            }
        }
        let retriesAttempted = 0;
        let checking = false;
        const check = async () => {
            var _a, _b, _c;
            if (!this.enabled) {
                return;
            }
            if (((_a = this.options.autoRecover) === null || _a === void 0 ? void 0 : _a.enabled) !== true) {
                return;
            }
            if (checking) {
                return;
            }
            checking = true;
            if (((_b = this.ws) === null || _b === void 0 ? void 0 : _b.readyState) !== OPEN && ((_c = this.ws) === null || _c === void 0 ? void 0 : _c.readyState) !== CONNECTING) {
                clearInterval(this.intervalHandle);
                try {
                    await this.recover();
                    retriesAttempted = 0;
                    if (this.options.debugMode) {
                        console.debug(`Auto recover done, recoveryState: ${this.connectionDetails.recoveryState}`);
                    }
                    this.eventEmitter.emit(this.connectionDetails.recoveryState === 'Successful'
                        ? Events.autoRecoverSuccess
                        : Events.autoRecoverFailed, this.ws);
                }
                catch (e) {
                    if (e instanceof RestException_1.default) {
                        throw e; // such as InsufficientPermissions
                    }
                    retriesAttempted += 1;
                    if (this.options.debugMode) {
                        console.debug('Auto recover error:', e);
                    }
                    this.eventEmitter.emit(Events.autoRecoverError, e);
                }
                this.intervalHandle = setInterval(check, this.options.autoRecover.checkInterval(retriesAttempted));
            }
            checking = false;
        };
        this.intervalHandle = setInterval(check, this.options.autoRecover.checkInterval(retriesAttempted));
        // browser only code start
        if (typeof window !== 'undefined' && window.addEventListener) {
            window.addEventListener('offline', () => {
                var _a;
                if (this.pingServerHandle) {
                    clearTimeout(this.pingServerHandle);
                }
                (_a = this.ws) === null || _a === void 0 ? void 0 : _a.close();
            });
            window.addEventListener('online', () => {
                check();
            });
        }
        // browser only code end
    }
    async recover() {
        if (this._recoverPromise) {
            return this._recoverPromise;
        }
        this._recoverPromise = this._recover();
        try {
            await this._recoverPromise;
        }
        finally {
            this._recoverPromise = undefined;
        }
        return undefined;
    }
    async _recover() {
        var _a, _b;
        if (((_a = this.ws) === null || _a === void 0 ? void 0 : _a.readyState) === OPEN || ((_b = this.ws) === null || _b === void 0 ? void 0 : _b.readyState) === CONNECTING) {
            return;
        }
        if (!this.wsc || !this.wsc.token) {
            await this.connect(false); // connect to WSG but do not recover
            return;
        }
        if (this.recoverTimestamp === undefined) {
            this.recoverTimestamp = Date.now();
        }
        if (this.connectionDetails !== undefined
            && Date.now() - this.recoverTimestamp
                > this.connectionDetails.recoveryTimeout * 1000) {
            if (this.options.debugMode) {
                console.debug('connect to WSG but do not recover');
            }
            await this.connect(false); // connect to WSG but do not recover
        }
        else {
            if (this.options.debugMode) {
                console.debug('connect to WSG and recover');
            }
            await this.connect(true); // connect to WSG and recover
        }
        this.recoverTimestamp = undefined;
        this.enable();
    }
    async pingServer() {
        var _a, _b, _c, _d, _e;
        if (((_a = this.options.autoRecover) === null || _a === void 0 ? void 0 : _a.enabled) !== true) {
            return;
        }
        try {
            if ((_b = this.ws) === null || _b === void 0 ? void 0 : _b.ping) {
                // node.js
                (_c = this.ws) === null || _c === void 0 ? void 0 : _c.ping();
            }
            else {
                // browser
                await ((_d = this.ws) === null || _d === void 0 ? void 0 : _d.send(JSON.stringify([
                    {
                        type: 'Heartbeat',
                        messageId: uuid(),
                    },
                ])));
            }
        }
        catch (e) {
            (_e = this.ws) === null || _e === void 0 ? void 0 : _e.close(); // Explicitly mark WS as closed
        }
    }
    async connect(recoverSession) {
        if (this._connectPromise) {
            return this._connectPromise;
        }
        this._connectPromise = this._connect(recoverSession);
        try {
            await this._connectPromise;
        }
        finally {
            this._connectPromise = undefined;
        }
        return undefined;
    }
    async _connect(recoverSession = false) {
        var _a;
        if (!this.wsToken || Date.now() > this.wsTokenExpiresAt) {
            const r = await this.rc.post('/restapi/oauth/wstoken');
            this.wsToken = r.data;
            // `expires_in` default value is 600 seconds. That's why we `* 0.8`
            this.wsTokenExpiresAt = Date.now() + (this.wsToken.expires_in * 0.8) * 1000;
        }
        let wsUri = `${this.wsToken.uri}?access_token=${this.wsToken.ws_access_token}`;
        if (recoverSession && this.wsc) {
            wsUri += `&wsc=${this.wsc.token}`;
        }
        this.ws = new isomorphic_ws_1.default(wsUri);
        this.eventEmitter.emit(Events.newWebSocketObject, this.ws);
        // override send method to wait for connecting
        const send = this.ws.send.bind(this.ws);
        this.ws.send = async (s) => {
            if (this.ws.readyState === CONNECTING) {
                await (0, wait_for_async_1.default)({
                    interval: 100,
                    condition: () => this.ws.readyState !== CONNECTING,
                });
            }
            await send(s);
        };
        if ((_a = this.options.autoRecover) === null || _a === void 0 ? void 0 : _a.enabled) {
            this.ws.addEventListener('message', () => {
                if (this.pingServerHandle) {
                    clearTimeout(this.pingServerHandle);
                }
                this.pingServerHandle = setTimeout(() => this.pingServer(), this.options.autoRecover.pingServerInterval);
            });
        }
        // debug mode to print all WebSocket traffic
        if (this.options.debugMode) {
            utils_1.default.debugWebSocket(this.ws);
        }
        // listen for new wsc data
        this.ws.addEventListener('message', (mEvent) => {
            const event = mEvent;
            const [meta, body] = utils_1.default.splitWsgData(event.data);
            if (meta.wsc
                && (!this.wsc
                    || (meta.type === 'ConnectionDetails' && body.recoveryState)
                    || this.wsc.sequence < meta.wsc.sequence)) {
                this.wsc = meta.wsc;
                this.eventEmitter.emit(Events.newWsc, this.wsc);
            }
        });
        // get initial ConnectionDetails data
        const [meta, body, event] = await utils_1.default.waitForWebSocketMessage(this.ws, (meta) => meta.type === 'ConnectionDetails' || meta.type === 'Error');
        if (meta.type === 'Error') {
            throw new ConnectionException_1.default(event);
        }
        this.connectionDetails = body;
        // fired when ws connection is ready for creating subscription
        this.eventEmitter.emit(Events.connectionReady, this.ws);
        // recover the subscription, if it exists and enabled
        if (this.subscription && this.subscription.enabled) {
            // because we have a new ws object
            this.subscription.setupWsEventListener();
            if (!recoverSession
                || this.connectionDetails.recoveryState === 'Failed') {
                // create new subscription if don't recover existing one
                await this.subscription.subscribe();
            }
        }
    }
    // keepInterval means we do not clear the interval
    async revoke(keepInterval = false) {
        var _a, _b;
        await ((_a = this.subscription) === null || _a === void 0 ? void 0 : _a.revoke());
        this.subscription = undefined;
        if (!keepInterval && this.intervalHandle) {
            clearInterval(this.intervalHandle);
        }
        if (this.pingServerHandle) {
            clearTimeout(this.pingServerHandle);
        }
        (_b = this.ws) === null || _b === void 0 ? void 0 : _b.close();
        this.wsc = undefined;
        this.wsToken = undefined;
        this.wsTokenExpiresAt = 0;
        this.disable();
    }
    async subscribe(eventFilters, callback, cache = undefined) {
        const subscription = new subscription_1.default(this, eventFilters, callback);
        if (cache === undefined || cache === null) {
            await subscription.subscribe();
        }
        else {
            subscription.subscriptionInfo = cache;
            await subscription.refresh();
        }
        this.subscription = subscription;
        return subscription;
    }
}
exports.default = WebSocketExtension;
//# sourceMappingURL=index.js.map